<template>
  <ValidationObserver ref="refValidationObserver">
    <div class="mb-3">
      <div :class="inline ? 'row align-items-start no-gutters' : ''">
        <div :class="inline ? 'col-12 col-md-5' : ''">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required"
          >
            <CustomRadio
              :error="errors[0]"
              name="compuMinuteSelection"
              :label="question"
              class="ml-n2"
              :value="useSystemMinute"
              :options="radioOptions"
              :is-inline="true"
              :is-disabled="disabled"
              @input="onRadioChange"
              custom-error-msg="Favor seleccionar una opción"
            />
          </ValidationProvider>
          <div
            v-if="onGoToDraftMinute && !disabled"
            class="mt-3 d-flex justify-content-end"
          >
            <ActionButton
              variant="outline-success"
              i="fas fa-file-signature"
              @click="handleGoToDraftMinute"
            >
              Ir a Redactar Minuta
            </ActionButton>
          </div>
        </div>
        <div
          v-if="useSystemMinute === true"
          :class="inline ? 'col-12 col-md-7' : 'mt-3 mx-3'"
        >
          <CustomError
            v-if="!isLoadingMinutes && minutes.length === 0"
            message="No se encontraron minutas en sistema para este estudiante."
            variant="warning"
            :is-alert-mode="true"
            class="mx-3"
          />
          <template v-else-if="!isLoadingMinutes">
            <div
              v-if="selectedOption && !isReplacing"
              class="row align-items-center no-gutters px-3 py-3 bg-secondary-3 border-radius--light"
            >
              <div class="col">
                <div class="color-secondary-6 font-14">{{ dropdownLabel }}</div>
                <div class="font-14">{{ selectedOption.text }}</div>
              </div>
              <div class="col-auto mx-4">
                <div class="color-secondary-6 font-14">Archivo:</div>
                <template v-if="fileAttachmentId">
                  <CustomFileInput
                    id="compuMinuteSelectedFile"
                    label=""
                    display-style="normal"
                    :disabled="disabled"
                    :attachment-id="fileAttachmentId"
                    :name="fileAttachmentName"
                    :on-download="onDownloadFile"
                    @after-remove="onDelete"
                  />
                </template>
                <span v-else class="color-secondary-4 font-14">Sin archivo</span>
              </div>
              <div v-if="!disabled" class="col-auto ml-auto mt-3">
                <ActionButton variant="success" @click="isReplacing = true">
                  Reemplazar Documento
                </ActionButton>
              </div>
            </div>
            <div
              v-else
              class="row align-items-end px-2 pt-3 pb-4 bg-secondary-3 border-radius--light"
            >
              <div :class="inline ? 'col-12' : 'col-6'">
                <ValidationProvider
                  v-slot="{ errors }"
                  rules="required"
                >
                  <CustomDropDown
                    id="compuMinuteDropdown"
                    :error="errors[0]"
                    :label="dropdownLabel"
                    :options="minuteOptions"
                    :value="selectedOption"
                    :disabled="disabled"
                    :clearable="false"
                    fieldkey="value"
                    fieldname="text"
                    :placeholder="dropdownPlaceholder"
                    @input="onMinuteSelect"
                    custom-error-msg="Favor seleccionar una minuta"
                  />
                </ValidationProvider>
              </div>
              <div v-if="isReplacing" class="col-auto align-self-end">
                <ActionButton variant="outline-danger" @click="isReplacing = false">
                  Cancelar
                </ActionButton>
              </div>
            </div>
          </template>
        </div>
        <div
          v-else-if="inline && useSystemMinute === false"
          class="col-12 col-md-7"
        >
          <slot name="manual-upload" />
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import CustomDropDown from 'custom-drop-down';
import CustomRadio from 'custom-radio';
import CustomError from 'custom-error';
import CustomFileInput from 'CustomFileInput';
import ActionButton from 'ActionButton';
import compuMinuteService from '@/modules/compu-minutes-editor/api';
import peiServices from '@/services/pei/general';

export default {
  name: 'CompuMinuteSelection',
  components: {
    CustomDropDown,
    CustomRadio,
    CustomError,
    CustomFileInput,
    ActionButton,
    ValidationObserver,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        useSystemMinute: null,
        compuMinuteId: null,
        fileId: null,
        fileName: null,
      }),
    },
    studentSieId: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onGoToDraftMinute: {
      type: Function,
      default: null,
    },
    question: {
      type: String,
      default: '¿Desea seleccionar una minuta redactada con el Editor Minuta del sistema MiPE?',
    },
    dropdownLabel: {
      type: String,
      default: 'Minuta:',
    },
    dropdownPlaceholder: {
      type: String,
      default: 'Seleccione una minuta',
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      useSystemMinute: this.value?.compuMinuteId || this.value?.useSystemMinute === true
        ? true
        : this.value?.fileId || this.value?.useSystemMinute === false
        ? false
        : null,
      minutes: [],
      isLoadingMinutes: false,
      isReplacing: false,
      radioOptions: [
        { id: 'yes', value: true, label: 'Sí' },
        { id: 'no', value: false, label: 'No' },
      ],
    };
  },
  async mounted() {
    if (this.useSystemMinute === true || this.value?.compuMinuteId) {
      await this.fetchMinutes();
    }
  },
  watch: {
    async studentSieId(newVal) {
      if (
        newVal &&
        this.useSystemMinute === true &&
        this.minutes.length === 0
      ) {
        await this.fetchMinutes();
      }
    },
    useSystemMinute(newVal) {
      if (newVal === false || this.fileAttachmentId == null) {
        this.$emit('input', {
          fileId: null, 
          fileName: null, 
          compuMinuteId: null,
          useSystemMinute: newVal
        });
      }

      this.$emit('selected-minute-changed', newVal ? this.selectedOption : null);
    },
    'value.compuMinuteId': {
      deep: true,
      immediate: true,
      async handler(newVal) {
        if (newVal && this.useSystemMinute === null) {
          this.useSystemMinute = true;
          await this.fetchMinutes();
        }

        this.$emit('selected-minute-changed', this.selectedOption);
      }
    },
    'value.useSystemMinute'(newVal) {
      // Cuando el padre resetea la respuesta externamente (ej. al cambiar de
      // disciplina sin desmontar este componente), sincronizamos el radio.
      if (newVal === null && this.useSystemMinute !== null) {
        this.useSystemMinute = null;
        this.isReplacing = false;
      }
    },
  },
  computed: {
    minuteOptions() {
      return this.minutes.map((m) => ({
        value: m.id,
        text: m.textSelection,
        signedDocumentId: m.signedDocumentId,
        signedDocumentFileName: m.signedDocumentFileName,
        meetingDate: m.meetingDate,
      }));
    },
    selectedOption() {
      if (!this.value?.compuMinuteId) return null;
      return (
        this.minuteOptions.find(
          (o) => o.value === Number(this.value.compuMinuteId)
        ) || null
      );
    },
    fileAttachmentId() {
      if (this.useSystemMinute === true) {
        return this.selectedOption?.signedDocumentId || null;
      }
      return this.value?.fileId || null;
    },
    fileAttachmentName() {
      if (this.useSystemMinute === true) {
        return this.selectedOption?.signedDocumentFileName || null;
      }
      return this.value?.fileName || null;
    },
  },
  methods: {
    async validate() {
      const result = await this.$refs.refValidationObserver.validate();

      // El dropdown de minutas no se renderiza mientras cargan ni cuando el estudiante
      // no tiene minutas en sistema, así que el observer no cubre esos casos por sí solo.
      if (this.useSystemMinute === true && !this.value?.compuMinuteId) {
        return false;
      }

      return result;
    },
    handleGoToDraftMinute() {
      if (!this.onGoToDraftMinute) return;
      this.onGoToDraftMinute();
    },
    async onRadioChange(value) {
      this.useSystemMinute = value;
      this.isReplacing = false;
      this.$emit('input', { useSystemMinute: value });
      if (value === true && this.minutes.length === 0) {
        await this.fetchMinutes();
      }
    },
    async fetchMinutes() {
      if (!this.studentSieId) return;
      this.isLoadingMinutes = true;
      try {
        const { data } = await compuMinuteService.getCompletedByStudent(
          this.studentSieId
        );
        this.minutes = data;
      } finally {
        this.isLoadingMinutes = false;
      }
    },
    onMinuteSelect(option) {
      const emitData = {
        compuMinuteId: option?.value ?? null,
        useSystemMinute: true,
        fileId: option?.signedDocumentId ?? null,
        fileName: option?.signedDocumentFileName ?? null,
      };
      
      this.$emit('input', emitData);
      if (option) this.isReplacing = false;
    },
    onDelete() {
      this.$emit('delete');
      this.$emit('input', {
        compuMinuteId: null,
        useSystemMinute: true,
        fileId: null,
        fileName: null
      });
    },
    onDownloadFile(attachmentId) {
      return Promise.resolve(
        peiServices.downloadDocumentByAttachmentId(attachmentId)
      );
    },
  },
};
</script>
